import { useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    Divider,
    IconButton,
    Table,
    TableContainer,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useTranslation } from 'react-i18next';
import { formatCentsToUsd } from '../../../../helpers/formatCentsToUsd';
import { definePrice, previewFinalPriceCents } from '../../../../helpers/calculateShowFees';

function SimplifiedTicketManager({
    updatedData,
    setUpdatedData,
    allTicketTypes,
    allTicketOptions,
    priceOverrides,
    showFees,
    isOnline,
    hasUnseatedTickets,
    delayedSeatAssignment,
    isTicketCombinationAllowed,
    netRate,
    commissionMatrix,
}) {
    const { t } = useTranslation();

    // Per-timeslot override wins over the ticket type's default matrix price.
    const resolvePrice = (ticketTypeId, ticketOptionId, priceMatrix, optionName) => {
        const override = priceOverrides?.get(`${ticketTypeId}:${ticketOptionId}`);
        return override !== undefined ? override : definePrice(priceMatrix, optionName);
    };

    // Non-guest-visible option IDs currently shown in the table
    const [addedToTable, setAddedToTable] = useState(new Set());

    const combos = (() => {
        const types = allTicketTypes || [];
        const options = allTicketOptions || [];
        const groups = [];
        for (const ticketType of types) {
            const items = [];
            for (const ticketOption of options) {
                if (
                    isTicketCombinationAllowed &&
                    !isTicketCombinationAllowed(ticketType.id, ticketOption.id)
                ) {
                    continue;
                }
                try {
                    const priceInCents = resolvePrice(
                        ticketType.id,
                        ticketOption.id,
                        ticketType.priceMatrix,
                        ticketOption.name,
                    );
                    if (priceInCents === undefined) continue;
                    // Net-rate agents are charged retail minus commission, not
                    // retail plus fees/taxes - show the amount that will
                    // actually be charged instead of the guest-facing price.
                    const feeOpts = {
                        netRate,
                        commissionMatrix,
                        ticketTypeId: ticketType.id,
                        ticketOptionId: ticketOption.id,
                        ticketOptionName: ticketOption.name,
                        showFees,
                        isOnline,
                    };
                    const finalPrice = previewFinalPriceCents(priceInCents, feeOpts);
                    // The show default (before any dynamic/override), so the
                    // agent sees the original price next to the effective one.
                    const basePriceInCents = definePrice(
                        ticketType.priceMatrix,
                        ticketOption.name,
                    );
                    const baseFinalPrice =
                        basePriceInCents === undefined
                            ? undefined
                            : previewFinalPriceCents(basePriceInCents, feeOpts);
                    items.push({
                        ticketTypeId: ticketType.id,
                        ticketTypeName: ticketType.name,
                        ticketOptionId: ticketOption.id,
                        ticketOptionName: ticketOption.name,
                        optionGuestVisible: ticketOption.guestVisible !== false,
                        priceInCents,
                        finalPrice,
                        baseFinalPrice,
                        canBuyAlone: ticketType.canBuyAlone,
                    });
                } catch (_e) {
                    // No price for this combo
                }
            }
            if (items.length > 0) {
                groups.push({ typeId: ticketType.id, typeName: ticketType.name, items });
            }
        }
        return groups;
    })();

    // Partition options that appear in at least one combo into guest-visible / non-guest-visible
    const { guestVisibleOptions, nonGuestVisibleOptions, nonGuestVisibleIds } = (() => {
        const seen = new Set();
        const gv = [];
        const ngv = [];
        const ngvIds = new Set();
        for (const group of combos) {
            for (const item of group.items) {
                if (seen.has(item.ticketOptionId)) continue;
                seen.add(item.ticketOptionId);
                const entry = { id: item.ticketOptionId, name: item.ticketOptionName };
                if (!item.optionGuestVisible) {
                    ngv.push(entry);
                    ngvIds.add(item.ticketOptionId);
                } else {
                    gv.push(entry);
                }
            }
        }
        return { guestVisibleOptions: gv, nonGuestVisibleOptions: ngv, nonGuestVisibleIds: ngvIds };
    })();

    // Fast lookup: `${typeId}:${optionId}` -> combo
    const comboMap = new Map();
    for (const group of combos) {
        for (const item of group.items) {
            comboMap.set(`${group.typeId}:${item.ticketOptionId}`, item);
        }
    }

    // Existing (already-saved) tickets keep the price they were sold at — they
    // are never re-priced on load. Only newly added tickets get the resolved
    // price (handleAdd + the combo "add" list). Re-pricing existing items here
    // would flag the booking dirty on every reload once dynamic pricing (which
    // returns a fresh price each time) is enabled.

    const activeTickets = updatedData.filter((item) => !item.shouldDelete);

    const countMap = {};
    for (const item of activeTickets) {
        const key = `${item.ticketTypeId}:${item.ticketOptionId}`;
        countMap[key] = (countMap[key] || 0) + 1;
    }

    const handleAdd = (combo) => {
        setUpdatedData((prev) => [
            ...prev,
            {
                id: Math.random().toString(36).substring(2, 15),
                ticketSeatId: null,
                ticketSeatName: '',
                ticketTypeId: combo.ticketTypeId,
                ticketTypeName: combo.ticketTypeName,
                ticketOptionId: combo.ticketOptionId,
                ticketOptionName: combo.ticketOptionName,
                customPriceCents: combo.priceInCents,
                price: combo.priceInCents,
                note: '',
                checkinNote: '',
                shouldDelete: false,
                ticketTypeCanBuyAlone: combo.canBuyAlone,
            },
        ]);
    };

    const handleRemove = (typeId, optionId) => {
        setUpdatedData((prev) => {
            const newIdx = prev.findLastIndex(
                (i) =>
                    !i.oldId &&
                    !i.shouldDelete &&
                    i.ticketTypeId === typeId &&
                    i.ticketOptionId === optionId,
            );
            if (newIdx !== -1) return prev.filter((_, idx) => idx !== newIdx);

            const existingIdx = prev.findLastIndex(
                (i) =>
                    i.oldId &&
                    !i.shouldDelete &&
                    i.ticketTypeId === typeId &&
                    i.ticketOptionId === optionId,
            );
            if (existingIdx !== -1) {
                return prev.map((item, idx) =>
                    idx === existingIdx ? { ...item, shouldDelete: true } : item,
                );
            }
            return prev;
        });
    };

    // Rows shown in table
    const tableOptions = [
        ...guestVisibleOptions,
        ...nonGuestVisibleOptions.filter((o) => addedToTable.has(o.id)),
    ];

    // Non-guest-visible options not currently in table
    const hiddenOptions = nonGuestVisibleOptions.filter((o) => !addedToTable.has(o.id));

    const addOptionToTable = (optionId) => {
        setAddedToTable((prev) => new Set([...prev, optionId]));
    };

    const removeOptionFromTable = (optionId) => {
        setAddedToTable((prev) => {
            const next = new Set(prev);
            next.delete(optionId);
            return next;
        });
    };

    return (
        <Box sx={{ width: '100%' }}>
            {hasUnseatedTickets && (
                <Alert severity="info" sx={{ mb: 1, py: 0 }} icon={false}>
                    {delayedSeatAssignment
                        ? t('bookingitems.simplified.seatsAssignedLater')
                        : t('bookingitems.simplified.autoSeatingOnSave')}
                </Alert>
            )}

            <TableContainer>
                <Table size="small" sx={{ tableLayout: 'auto', width: '100%' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={{
                                    borderBottom: '2px solid',
                                    borderColor: 'divider',
                                    pl: 0,
                                    minWidth: 120,
                                }}
                            />
                            {combos.map((group) => (
                                <TableCell
                                    key={group.typeId}
                                    align="center"
                                    sx={{
                                        fontWeight: 600,
                                        borderBottom: '2px solid',
                                        borderColor: 'divider',
                                        whiteSpace: 'nowrap',
                                        minWidth: 140,
                                    }}
                                >
                                    {group.typeName}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableOptions.map((option) => {
                            const isNonGuest = nonGuestVisibleIds.has(option.id);
                            return (
                                <TableRow
                                    key={option.id}
                                    sx={{ '&:last-child td, &:last-child th': { borderBottom: 0 } }}
                                >
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        sx={{ pl: 0, fontWeight: 500 }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                minWidth: 0,
                                            }}
                                        >
                                            <Box
                                                component="span"
                                                sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    minWidth: 0,
                                                    flexShrink: 1,
                                                }}
                                            >
                                                {option.name}
                                            </Box>
                                            {isNonGuest && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => removeOptionFromTable(option.id)}
                                                    sx={{ p: 0.25, color: 'text.disabled' }}
                                                    title={t(
                                                        'bookingitems.simplified.removeFromTable',
                                                    )}
                                                >
                                                    <CloseIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </TableCell>
                                    {combos.map((group) => {
                                        const combo = comboMap.get(`${group.typeId}:${option.id}`);
                                        if (!combo) {
                                            return <TableCell key={group.typeId} />;
                                        }
                                        const count =
                                            countMap[
                                                `${combo.ticketTypeId}:${combo.ticketOptionId}`
                                            ] || 0;
                                        return (
                                            <TableCell
                                                key={group.typeId}
                                                align="center"
                                                sx={{ px: 1 }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 0.25,
                                                    }}
                                                >
                                                    {combo.baseFinalPrice !== undefined &&
                                                    combo.baseFinalPrice !== combo.finalPrice ? (
                                                        <Tooltip
                                                            title={t(
                                                                'bookingitems.simplified.dynamicPriceHint',
                                                                {
                                                                    base: formatCentsToUsd(
                                                                        combo.baseFinalPrice,
                                                                    ),
                                                                },
                                                            )}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'flex-end',
                                                                    lineHeight: 1.15,
                                                                    minWidth: 48,
                                                                    mr: 0.5,
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    color="text.secondary"
                                                                    sx={{ fontWeight: 600 }}
                                                                >
                                                                    {formatCentsToUsd(
                                                                        combo.finalPrice,
                                                                    )}
                                                                </Typography>
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 0.25,
                                                                    }}
                                                                >
                                                                    {combo.finalPrice >
                                                                    combo.baseFinalPrice ? (
                                                                        <ArrowUpwardIcon
                                                                            sx={{
                                                                                fontSize: 11,
                                                                                color: 'warning.main',
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <ArrowDownwardIcon
                                                                            sx={{
                                                                                fontSize: 11,
                                                                                color: 'success.main',
                                                                            }}
                                                                        />
                                                                    )}
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.disabled"
                                                                    >
                                                                        {formatCentsToUsd(
                                                                            combo.baseFinalPrice,
                                                                        )}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Tooltip>
                                                    ) : (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{
                                                                minWidth: 48,
                                                                textAlign: 'right',
                                                                mr: 0.5,
                                                            }}
                                                        >
                                                            {formatCentsToUsd(combo.finalPrice)}
                                                        </Typography>
                                                    )}
                                                    <IconButton
                                                        size="small"
                                                        disabled={count === 0}
                                                        onClick={() =>
                                                            handleRemove(
                                                                combo.ticketTypeId,
                                                                combo.ticketOptionId,
                                                            )
                                                        }
                                                        sx={{ p: 0.25 }}
                                                    >
                                                        <RemoveIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            width: 24,
                                                            textAlign: 'center',
                                                            fontWeight: count > 0 ? 600 : 400,
                                                        }}
                                                    >
                                                        {count}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleAdd(combo)}
                                                        sx={{ p: 0.25 }}
                                                    >
                                                        <AddIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {hiddenOptions.length > 0 && (
                <Box sx={{ mt: 1 }}>
                    <Divider sx={{ mb: 1 }} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {hiddenOptions.map((option) => {
                            const totalCount = combos.reduce((sum, group) => {
                                const combo = comboMap.get(`${group.typeId}:${option.id}`);
                                if (!combo) return sum;
                                return (
                                    sum +
                                    (countMap[`${combo.ticketTypeId}:${combo.ticketOptionId}`] || 0)
                                );
                            }, 0);
                            return (
                                <Chip
                                    key={option.id}
                                    label={
                                        totalCount > 0
                                            ? `${option.name} (${totalCount})`
                                            : option.name
                                    }
                                    size="small"
                                    variant="outlined"
                                    icon={<AddIcon />}
                                    onClick={() => addOptionToTable(option.id)}
                                    title={t('bookingitems.simplified.addToTable')}
                                    sx={{ cursor: 'pointer' }}
                                />
                            );
                        })}
                    </Box>
                </Box>
            )}
        </Box>
    );
}

export default SimplifiedTicketManager;
